import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { listTenants, getMonthlyUsage, updateTenant, generateConsoleLink, generateShortLink, getPlatformConfig, resetPortalPassword, listPartners, listSeats, createSeatUser, seatAction, setQuota } from "../lib/api";
import type { SeatsView } from "../lib/api";
import type { Tenant, Partner } from "../types";
import TenantFeaturesPage from "./TenantFeaturesPage";

const WA_CONNECTOR_URL = "https://aibot-wa.jimsbond007.workers.dev";

// 公開主域走 gateway 子路徑（2026-09-07 域收斂 wtsaibot.com；連結格式 ?t=&n=&w= 不變）
const WIZARD_BASE = "https://wtsaibot.com/setup";

function wizardLink(t: Partial<Tenant>): string {
  const params = new URLSearchParams({ t: String(t.tenant_id || "") });
  if (t.bot_name) params.set("n", t.bot_name);
  if (t.webhook_token) params.set("w", t.webhook_token);
  return `${WIZARD_BASE}/?${params.toString()}`;
}

function bindLink(t: Partial<Tenant>): string {
  const params = new URLSearchParams({ t: String(t.tenant_id || "") });
  if (t.webhook_token) params.set("w", t.webhook_token);
  return `${WIZARD_BASE}/bind?${params.toString()}`;
}

const TABS = [
  { id: "settings", label: "⚙️ 基本設定" },
  { id: "quota", label: "💰 訂閱與配額" },
  { id: "wizard", label: "🧙 開通連結" },
  { id: "bind", label: "📱 綁定連結" },
  { id: "features", label: "🧩 功能" },
] as const;

type TabId = (typeof TABS)[number]["id"];

// ── System Prompt 預設模板 ──
const PROMPT_TEMPLATES: { label: string; icon: string; prompt: string }[] = [
  {
    label: "客服通用",
    icon: "🎧",
    prompt:
      "你是一位專業的客戶服務代表。請以友善、耐心、簡潔的方式回答客戶問題。如果問題超出你的知識範圍，請禮貌地引導客戶聯繫人工客服。使用繁體中文回覆。",
  },
  {
    label: "銷售顧問",
    icon: "💼",
    prompt:
      "你是一位熱情的銷售顧問。你的目標是了解客戶需求，推薦最適合的產品或服務，並促成交易。語氣要親切專業，適時提出問題以挖掘需求。使用繁體中文回覆。",
  },
  {
    label: "技術支援",
    icon: "🔧",
    prompt:
      "你是一位技術支援工程師。請以清晰、有條理的方式協助用戶解決技術問題。如果問題需要進一步排查，請引導用戶提供相關資訊（如錯誤訊息、操作步驟）。使用繁體中文回覆。",
  },
  {
    label: "餐飲服務",
    icon: "🍽️",
    prompt:
      "你是一間餐廳的親切服務員。請熱情地介紹菜式、回答關於菜單和訂位的問題，並主動推薦招牌菜。語氣要溫暖有禮。使用繁體中文回覆。",
  },
  {
    label: "教育培訓",
    icon: "📚",
    prompt:
      "你是一位耐心的教育顧問。請根據學生或家長的需求，介紹適合的課程和學習方案。回答要詳盡但易懂，鼓勵學習熱情。使用繁體中文回覆。",
  },
  {
    label: "醫療健康",
    icon: "🏥",
    prompt:
      "你是一位專業的醫療諮詢助手。請提供準確、易懂的健康資訊，但必須強調這不能取代專業醫生的診斷和建議。語氣要溫和、專業。使用繁體中文回覆。",
  },
  {
    label: "清空",
    icon: "🧹",
    prompt: "",
  },
];

// ── 報價單收款／品牌欄位（quote_pdf 多客化；JSON 存 tenants.quote_branding）──
const BRANDING_FIELDS: { key: string; label: string; placeholder: string }[] = [
  { key: "chequeTitle", label: "支票抬頭（英文全名）", placeholder: "E-CORP SOLUTION LIMITED" },
  { key: "bank", label: "銀行", placeholder: "中國銀行（香港）" },
  { key: "account", label: "銀行帳號", placeholder: "012-878-1028 XXXX" },
  { key: "fps", label: "FPS 轉數快", placeholder: "9874 2621" },
  { key: "signCompany", label: "簽署公司名（For and on behalf of）", placeholder: "E-Corp Solution Limited" },
  { key: "footerLine1", label: "頁腳第一行（公司｜網站｜電郵｜電話）", placeholder: "E-Corp Solution Limited 盈滙企業方案有限公司｜www.e-corpsolution.com｜info@e-corpsolution.com｜3987 1036" },
  { key: "footerLine2", label: "頁腳第二行（地址）", placeholder: "香港新界荃灣青山公路 264-298 號南豐中心 23 樓 2301 B3-B4 室" },
];

export default function TenantEditPage() {
  const { tenantId } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const tab: TabId = TABS.some((t) => t.id === tabParam) ? (tabParam as TabId) : "settings";
  const setTab = (id: TabId) => setSearchParams(id === "settings" ? {} : { tab: id }, { replace: true });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<any>(null);

  const [form, setForm] = useState<Partial<Tenant>>({});
  // 月度配額：本月用量（唯讀顯示）＋人手 recharge 欄位
  const [monthlyUsage, setMonthlyUsage] = useState<{ ym: string; rows?: { ai_replies: number; quota: number | null; overage: number; due_hkd: number }[] } | null>(null);
  const [webhookCopied, setWebhookCopied] = useState(false);
  // 報價單品牌：由 quote_branding JSON 拆欄位編輯，儲存時重新組 JSON
  const [branding, setBranding] = useState<Record<string, string>>({});
  // 租戶署名（兩層設計）：開關 Off＝冇署名；On＋空＝平台預設；On＋有內容＝自訂
  const [brandOn, setBrandOn] = useState(false);
  const [brandText, setBrandText] = useState("");
  const [platformBrand, setPlatformBrand] = useState("© AIBot");
  // 商戶門戶密碼重置（一鍵複製 WhatsApp 格式登入資料；sendNote＝直接發送結果）
  const [portal, setPortal] = useState<{ loading: boolean; msg: string | null; error: string | null; copied: boolean; sendNote?: string | null }>({
    loading: false, msg: null, error: null, copied: false,
  });
  // 戰略夥伴下拉（P1 §2.3）：渠道夥伴掛鉤
  const [partners, setPartners] = useState<Partner[]>([]);
  // 員工席位（Phase C 2026-10-10：HK$80/席/月 KPay 直收無試用期；admin 審批流）
  const [seats, setSeats] = useState<SeatsView | null>(null);
  const [seatsBusy, setSeatsBusy] = useState(false);
  const [seatsNote, setSeatsNote] = useState<{ kind: "ok" | "err"; text: string } | null>(null);
  const [seatUsername, setSeatUsername] = useState("");
  const [seatDisplayName, setSeatDisplayName] = useState("");
  const [seatQty, setSeatQty] = useState(1);
  const [seatPayUrl, setSeatPayUrl] = useState<string | null>(null);
  const [seatPayCopied, setSeatPayCopied] = useState(false);
  // 訂閱與配額 TAB（IA 四組收斂 2026-10-19）：trial_quota 由舊 /quota 頁併入，逐租戶編輯
  const [quotaValue, setQuotaValue] = useState<number>(0);
  const [quotaSaving, setQuotaSaving] = useState(false);
  const [quotaMsg, setQuotaMsg] = useState<{ ok: boolean; text: string } | null>(null);

  async function reloadSeats() {
    if (!tenantId) return;
    try {
      const d = await listSeats(tenantId);
      setSeats(d);
    } catch {
      setSeats({ ok: false, error: "載入失敗" });
    }
  }

  // 訂閱與配額 TAB：儲存 trial_quota（原 /quota 頁邏輯，逐租戶版）
  async function handleQuotaSave() {
    if (!tenantId) return;
    setQuotaSaving(true);
    setQuotaMsg(null);
    try {
      const data = await setQuota(tenantId, quotaValue);
      if (data.success) {
        setForm((prev) => ({ ...prev, trial_quota: data.quota }));
        setQuotaMsg({ ok: true, text: `✅ 已設為 ${data.quota}` });
      } else {
        setQuotaMsg({ ok: false, text: `❌ ${data.error || "失敗"}` });
      }
    } catch {
      setQuotaMsg({ ok: false, text: "❌ 網路錯誤" });
    } finally {
      setQuotaSaving(false);
    }
  }

  async function handleSeatCreate() {
    if (!tenantId || !seatUsername.trim()) return;
    setSeatsBusy(true);
    setSeatsNote(null);
    try {
      const r = await createSeatUser(tenantId, seatUsername.trim(), seatDisplayName.trim());
      if (r.ok) {
        setSeatsNote({ kind: "ok", text: `✅ 席位已開：帳號 ${r.username}／臨時密碼 ${r.tempPassword}（只顯示一次，立即複製）` });
        setSeatUsername("");
        setSeatDisplayName("");
        reloadSeats();
      } else {
        setSeatsNote({ kind: "err", text: `⚠️ ${r.error || "開戶失敗"}` });
      }
    } catch {
      setSeatsNote({ kind: "err", text: "⚠️ 開戶請求失敗" });
    } finally {
      setSeatsBusy(false);
    }
  }

  async function handleSeatAction(action: "reset" | "status" | "delete" | "order", body: { id?: number; status?: string; seats?: number }, confirmText?: string) {
    if (!tenantId || (confirmText && !confirm(confirmText))) return;
    setSeatsBusy(true);
    setSeatsNote(null);
    setSeatPayCopied(false);
    try {
      const r = await seatAction(tenantId, action, body);
      if (r.ok) {
        if (action === "reset") {
          setSeatsNote({ kind: "ok", text: `✅ 密碼已重設：臨時密碼 ${r.tempPassword}（只顯示一次，立即複製）` });
        } else if (action === "order") {
          setSeatPayUrl(r.payUrl || null);
          setSeatsNote({ kind: "ok", text: `✅ 訂單已開：${r.orderId}（HK$${r.amountHkd}）——複製付款連結交商戶` });
        } else {
          setSeatsNote({ kind: "ok", text: "✅ 已更新" });
        }
        reloadSeats();
      } else {
        setSeatsNote({ kind: "err", text: `⚠️ ${r.error || "操作失敗"}` });
      }
    } catch {
      setSeatsNote({ kind: "err", text: "⚠️ 操作請求失敗" });
    } finally {
      setSeatsBusy(false);
    }
  }


  useEffect(() => {
    if (!tenantId) return;
    loadTenant();
    reloadSeats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  async function loadTenant() {
    setLoading(true);
    try {
      const data = await listTenants();
      listPartners()
        .then((d) => setPartners(d.partners || []))
        .catch(() => {});
      getMonthlyUsage(tenantId)
        .then((d) => setMonthlyUsage(d))
        .catch(() => {});
      getPlatformConfig("brand_footer")
        .then((d) => {
          if (d.exists && d.value) setPlatformBrand(d.value);
        })
        .catch(() => {});
      if (data.success) {
        const t = data.tenants.find((x: Tenant) => x.tenant_id === tenantId);
        if (t) {
          setForm(t);
          setQuotaValue(t.trial_quota ?? 0);
          setBrandOn(t.brand_footer_enabled === 1);
          setBrandText(t.brand_footer_text || "");
          try {
            const parsed = t.quote_branding ? JSON.parse(t.quote_branding) : {};
            if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
              setBranding(parsed as Record<string, string>);
            }
          } catch {
            // 舊值唔係 JSON——由空欄位開始（儲存時會以新值覆蓋）
          }
        } else {
          setError("租戶不存在");
        }
      }
    } catch {
      setError("載入失敗");
    } finally {
      setLoading(false);
    }
  }

  const handleChange = (field: keyof Tenant, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tenantId) return;

    setSaving(true);
    setResult(null);
    setError("");

    try {
      const updates: Record<string, string> = {};
      const fields: (keyof Tenant)[] = [
        "bot_name",
        "system_prompt",
        "plan_type",
        "plan_tier",
        "monthly_quota_override",
        "premium_until",
        "contact_handoff",
        "llm_provider",
        "llm_model",
        "llm_api_key",
        "whatsapp_api_key",
        "whatsapp_sender_number",
        "rpmsms_base_url",
        "secretary_prompt",
      ];
      for (const f of fields) {
        if (form[f] !== undefined) {
          // SEC-2026-09 R5：列表 API 回傳遮罩值（••••尾4位）——原樣唔送＝留空保留現有 Key
          // 2026-09-26：防 String(null) 寫入字串 "null"——null/undefined 唔送，保持後端 NULL
          const raw = form[f];
          if (raw === null || raw === undefined) {
            continue;
          }
          const v = String(raw);
          if ((f === "llm_api_key" || f === "whatsapp_api_key") && v.startsWith("••••")) {
            continue;
          }
          updates[f] = v;
        }
      }

      // 報價單品牌：逐欄位收集→JSON；全空＝空字串（後端清為 null→PDF 用平台預設）
      const filledBranding = Object.fromEntries(
        Object.entries(branding).filter(([, v]) => v.trim() !== "")
      );
      updates.quote_branding = Object.keys(filledBranding).length
        ? JSON.stringify(filledBranding)
        : "";
      // 租戶署名（兩層設計）：開關＋內容
      updates.brand_footer_enabled = brandOn ? "1" : "0";
      updates.brand_footer_text = brandText;
      // 語音功能雙開關（角色化架構；/merchant radio 受此約束）
      updates.cs_enabled = Number(form.cs_enabled ?? 1) === 1 ? "1" : "0";
      updates.secretary_enabled = Number(form.secretary_enabled ?? 0) === 1 ? "1" : "0";
      updates.role_chosen = Number(form.role_chosen ?? 1) === 1 ? "1" : "0";
      // 戰略夥伴掛鉤（P1）：空字串＝解除掛鉤（後端轉 SQL NULL）
      updates.partner_id = (form.partner_id || "").trim();

      const data = await updateTenant(tenantId, updates);
      setResult(data);
    } catch {
      setError("儲存失敗");
    } finally {
      setSaving(false);
    }
  };

  const applyTemplate = (prompt: string) => {
    handleChange("system_prompt", prompt);
  };

  // 門戶登入資料：純文字格式（WhatsApp 貼上零變形——唔用任何 markdown 符號）
  const buildPortalMessage = (pwd: string) =>
    `您好！以下係貴公司 AI 客服門戶嘅登入資料：\n\n` +
    `入口網址：https://wtsaibot.com/merchant\n` +
    `帳號：${tenantId}\n` +
    `臨時密碼：${pwd}\n\n` +
    `首次登入後請立即自設新密碼。\n` +
    `如有任何疑問，歡迎隨時聯絡我們，謝謝！`;

  const handlePortalReset = async (sendToMerchant = false) => {
    if (!tenantId) return;
    const verb = sendToMerchant ? "重置並直接發送俾商戶登記手機" : "重置";
    if (!confirm(`確定${verb}「${tenantId}」嘅門戶密碼？\n\n舊密碼會即刻作廢——如果客人正用緊舊密碼，會登入唔到。`)) return;
    setPortal({ loading: true, msg: null, error: null, copied: false, sendNote: null });
    try {
      const data = await resetPortalPassword(tenantId, sendToMerchant);
      const msg = buildPortalMessage(data.password);
      let copied = false;
      try {
        await navigator.clipboard.writeText(msg);
        copied = true;
      } catch {
        // 自動複製失敗（權限／非 https）——畫面仍有全文可手動複製
      }
      // 直接發送（2026-09-11）：成功＝綠確認；失敗跌剪貼板（剪貼板永遠做後備）
      const sendNote = sendToMerchant
        ? data.sent
          ? `已直接發送俾商戶登記手機（尾4位 ${data.sentTo || "****"}）——剪貼板已同時備份`
          : `自動發送失敗（${data.sendError || "未知原因"}）——已複製，手動貼俾客戶就得`
        : null;
      setPortal({ loading: false, msg, error: null, copied, sendNote });
    } catch (e) {
      setPortal({ loading: false, msg: null, error: e instanceof Error ? e.message : "重置失敗", copied: false, sendNote: null });
    }
  };

  const copyPortalAgain = async () => {
    if (!portal.msg) return;
    try {
      await navigator.clipboard.writeText(portal.msg);
      setPortal((p) => ({ ...p, copied: true }));
    } catch {
      // 靜默——用戶可手動選取
    }
  };

  const [consoleToken, setConsoleToken] = useState<string | null>(null);
  const [consoleLoading, setConsoleLoading] = useState(false);
  const [consoleError, setConsoleError] = useState<string | null>(null);

  const [shortUrl, setShortUrl] = useState<string | null>(null);

  const handleGenerateConsoleLink = async () => {
    if (!tenantId) return;
    setConsoleLoading(true);
    setConsoleError(null);
    setShortUrl(null);
    try {
      // 1. 產生/重新產生控制台 token
      const linkData = await generateConsoleLink(tenantId);
      if (!linkData.success) {
        setConsoleError(linkData.error || "產生失敗");
        setConsoleLoading(false);
        return;
      }
      setConsoleToken(linkData.token);

      // 2. 產生 8 位短連結
      const shortData = await generateShortLink(tenantId);
      if (shortData.success && shortData.shortUrl) {
        setShortUrl(shortData.shortUrl);
      } else {
        setConsoleError(shortData.error || "短連結產生失敗");
      }
    } catch {
      setConsoleError("產生失敗，請稍後再試");
    } finally {
      setConsoleLoading(false);
    }
  };

  if (loading) return <div className="text-slate-400">載入中...（超過 20 秒會自動轉錯誤提示）</div>;
  if (error && !form.tenant_id)
    return (
      <div className="space-y-3">
        <div className="text-rose-400">❌ {error}</div>
        {/* 2026-10-27（Captain 實錄「空白一片」）：瞬時網絡失敗此前只剩一行紅字、無路可走——加重試掣 */}
        <button
          onClick={() => {
            setError("");
            void loadTenant();
            void reloadSeats();
          }}
          className="px-4 py-1.5 text-sm bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-lg"
        >
          🔄 重新載入
        </button>
      </div>
    );

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate("/tenants")}
          className="px-3 py-1.5 text-sm bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg transition-colors"
        >
          ← 返回
        </button>
        <h1 className="text-2xl font-bold text-slate-100">
          ✏️ 編輯租戶：{tenantId}
        </h1>
      </div>

      {/* Tab bar */}
      <div className="flex items-center gap-1 mb-6 border-b border-slate-800">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors border-b-2 -mb-px ${
              tab === t.id
                ? "border-emerald-500 text-emerald-400 bg-slate-900"
                : "border-transparent text-slate-400 hover:text-slate-200 hover:bg-slate-900/50"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "wizard" && (
        <div className="max-w-2xl p-4 bg-amber-900/20 border border-amber-800/30 rounded-xl">
          <h3 className="text-sm font-semibold text-amber-400 mb-2">
            🧙 阿Bot師傅開通連結
          </h3>
          <p className="text-xs text-slate-400 mb-3 leading-relaxed">
            客戶點開即為此租戶專屬對話，由阿Bot師傅用 15 分鐘完成人格＋知識庫設定。連結帶 <code className="text-amber-300">w</code> 參數（不可猜安裝權限），請直接成條轉發，唔好截斷。
          </p>
          <div className="mb-3 p-3 bg-slate-950 rounded-lg border border-amber-700/30">
            <code className="block text-sm font-mono text-amber-300 break-all select-all">
              {wizardLink(form)}
            </code>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigator.clipboard.writeText(wizardLink(form)).catch(() => {})}
              className="px-3 py-1.5 text-xs bg-amber-700/50 hover:bg-amber-700/70 text-amber-100 rounded-lg transition-colors"
            >
              📋 複製連結
            </button>
            <button
              type="button"
              onClick={() => window.open(wizardLink(form), "_blank", "noopener")}
              className="px-3 py-1.5 text-xs bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg transition-colors"
            >
              ↗️ 開啟試睇
            </button>
          </div>
        </div>
      )}

      {tab === "bind" && (
        <div className="max-w-2xl p-4 bg-teal-900/20 border border-teal-800/30 rounded-xl">
          <h3 className="text-sm font-semibold text-teal-400 mb-2">
            📱 WhatsApp 自助綁定連結
          </h3>
          <p className="text-xs text-slate-400 mb-3 leading-relaxed">
            客戶自己掃 QR 完成 WhatsApp 綁定。<span className="text-amber-400">前提是先喺「基本設定」填好發送號碼同 API Key</span>，否則綁定完成都發唔出訊息。
          </p>
          <div className="mb-3 p-3 bg-slate-950 rounded-lg border border-teal-700/30">
            <code className="block text-sm font-mono text-teal-300 break-all select-all">
              {bindLink(form)}
            </code>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigator.clipboard.writeText(bindLink(form)).catch(() => {})}
              className="px-3 py-1.5 text-xs bg-teal-700/50 hover:bg-teal-700/70 text-teal-100 rounded-lg transition-colors"
            >
              📋 複製連結
            </button>
            <button
              type="button"
              onClick={() => window.open(bindLink(form), "_blank", "noopener")}
              className="px-3 py-1.5 text-xs bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg transition-colors"
            >
              ↗️ 開啟試睇
            </button>
          </div>
        </div>
      )}

      {tab === "features" && <TenantFeaturesPage embedded />}

      {tab === "quota" && (
        <div className="max-w-2xl space-y-4">
          {/* 訂閱狀態（唯讀速覽；改方案去「基本設定」） */}
          <div className="p-4 bg-slate-900 border border-slate-800 rounded-xl">
            <h3 className="text-sm font-semibold text-slate-200 mb-3">📋 訂閱狀態</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="text-slate-400">方案類型<div className="text-slate-100 font-medium mt-0.5">{form.plan_type || "—"}</div></div>
              <div className="text-slate-400">方案等級<div className="text-slate-100 font-medium mt-0.5">{form.plan_tier || "—"}</div></div>
              <div className="text-slate-400">有效期至<div className="text-slate-100 font-medium mt-0.5">{form.premium_until ? new Date(form.premium_until).toLocaleDateString("zh-HK") : "—"}</div></div>
              <div className="text-slate-400">本月用量<div className="text-slate-100 font-medium mt-0.5">{monthlyUsage?.rows?.[0] ? `${monthlyUsage.rows[0].ai_replies} 則` : "—"}</div></div>
            </div>
            <p className="text-xs text-slate-500 mt-3">月度配額（Lite 2,000／Pro 6,000 則）以方案為準；改方案請到「⚙️ 基本設定」。</p>
          </div>
          {/* 試用額度（原 /quota 頁功能，IA 收斂併入 2026-10-19） */}
          <div className="p-4 bg-slate-900 border border-slate-800 rounded-xl">
            <h3 className="text-sm font-semibold text-slate-200 mb-2">💰 試用額度（trial_quota）</h3>
            <p className="text-xs text-slate-500 mb-3">試用／開通期一次性額度，只影響本租戶。</p>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={0}
                value={quotaValue}
                onChange={(e) => setQuotaValue(parseInt(e.target.value) || 0)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleQuotaSave(); } }}
                className="w-28 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-100 text-sm focus:outline-none focus:border-emerald-600"
              />
              <button
                type="button"
                onClick={handleQuotaSave}
                disabled={quotaSaving}
                className="px-4 py-2 text-sm bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-700 text-white rounded-lg transition-colors"
              >
                {quotaSaving ? "儲存中..." : "儲存"}
              </button>
              {quotaMsg && (
                <span className={`text-xs font-medium ${quotaMsg.ok ? "text-emerald-400" : "text-rose-400"}`}>{quotaMsg.text}</span>
              )}
            </div>
          </div>
        </div>
      )}

      {tab === "settings" && (<>
      {/* Webhook URL 顯示 */}
      <div className="mb-6 p-4 bg-emerald-900/20 border border-emerald-800/30 rounded-xl">
        <h3 className="text-sm font-semibold text-emerald-400 mb-2">
          🔗 WhatsApp Webhook URL（請複製到 WhatsApp 後台設定）
        </h3>
        <div className="flex items-center gap-3">
          <code className="flex-1 block p-3 bg-slate-950 rounded-lg text-xs font-mono text-emerald-300 break-all">
            {WA_CONNECTOR_URL}/webhook/{form.webhook_token || "（載入中…）"}
          </code>
          <button
            type="button"
            onClick={() => {
              navigator.clipboard
                .writeText(`${WA_CONNECTOR_URL}/webhook/${form.webhook_token || ""}`)
                .then(() => {
                  setWebhookCopied(true);
                  setTimeout(() => setWebhookCopied(false), 2000);
                })
                .catch(() => {});
            }}
            className="px-3 py-2 text-xs font-medium rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white transition-colors shrink-0"
          >
            {webhookCopied ? "已複製 ✓" : "複製"}
          </button>
        </div>
      </div>

      {/* 商戶門戶登入資料（一鍵重置＋複製 WhatsApp 格式） */}
      <div className="mb-6 p-4 bg-sky-900/20 border border-sky-800/30 rounded-xl">
        <h3 className="text-sm font-semibold text-sky-400 mb-2">
          🔑 商戶門戶登入資料
        </h3>
        <p className="text-xs text-slate-400 mb-3 leading-relaxed">
          一鍵重置門戶密碼，並以 WhatsApp 純文字格式（入口＋帳號＋臨時密碼）自動複製到剪貼板，直接貼俾客戶就得。
          <span className="text-amber-400">每次重置都會令舊密碼即刻作廢。</span>
          客人首次登入後系統會強制佢自設新密碼。
        </p>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => handlePortalReset(false)}
            disabled={portal.loading}
            className="px-4 py-2 text-sm bg-sky-700/50 hover:bg-sky-700/70 disabled:bg-slate-700 text-sky-100 rounded-lg transition-colors"
          >
            {portal.loading ? "重置中…" : "🔑 重置並複製登入資料"}
          </button>
          <button
            type="button"
            onClick={() => handlePortalReset(true)}
            disabled={portal.loading || !(form.contact_handoff || "").trim()}
            title={!(form.contact_handoff || "").trim() ? "呢個租戶冇登記手機（contact_handoff）——先喺下方填好先用到" : "經中央監控號直接發送登入資料去租戶登記手機"}
            className="px-4 py-2 text-sm bg-emerald-700/50 hover:bg-emerald-700/70 disabled:bg-slate-700 disabled:cursor-not-allowed text-emerald-100 rounded-lg transition-colors"
          >
            {portal.loading ? "處理中…" : "📲 重置並直接發送俾商戶"}
          </button>
        </div>
        {!(form.contact_handoff || "").trim() && (
          <p className="text-xs text-slate-500 mt-2">（冇登記手機——直接發送掣封鎖中；填好 contact_handoff 後可用）</p>
        )}
        {portal.error && (
          <p className="text-xs text-rose-400 mt-2">⚠️ {portal.error}</p>
        )}
        {portal.msg && (
          <div className="mt-3 p-3 bg-slate-950 rounded-lg border border-sky-700/30">
            <div className="flex items-center justify-between mb-2">
              <p className={`text-xs ${portal.copied ? "text-emerald-400" : "text-amber-400"}`}>
                {portal.copied
                  ? "✅ 已複製到剪貼板——直接去 WhatsApp 貼就得"
                  : "請手動全選下面文字複製"}
              </p>
              {portal.sendNote && (
                <p className={`text-xs ${portal.sendNote.startsWith("已直接") ? "text-emerald-400" : "text-amber-400"}`}>
                  {portal.sendNote.startsWith("已直接") ? "📲 " : "⚠️ "}{portal.sendNote}
                </p>
              )}
              {!portal.copied && (
                <button
                  type="button"
                  onClick={copyPortalAgain}
                  className="text-xs px-2 py-1 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded"
                >
                  📋 重新複製
                </button>
              )}
            </div>
            <pre className="whitespace-pre-wrap text-sm text-emerald-200 font-mono select-all leading-relaxed">
              {portal.msg}
            </pre>
          </div>
        )}
      </div>

      {/* 員工席位（Phase C 2026-10-10：HK$80/席/月 KPay 直收無試用期；admin 審批流先行，Merchant 界面暫不上） */}
      <div className="mb-6 p-4 bg-violet-900/20 border border-violet-800/30 rounded-xl">
        <h3 className="text-sm font-semibold text-violet-300 mb-2">👥 員工席位（多登入）</h3>
        <p className="text-xs text-slate-400 mb-3 leading-relaxed">
          權益：Pro 含 <span className="text-slate-200">2 員工席</span>；Lite／legacy＝0 席。超出權益 HK$80／席／月（KPay 收款，無試用期）。
          <span className="text-amber-400"> 商戶門戶 operator 登入接線等 Merchant 界面 GO 後啟用——帳號先行開立備用。</span>
        </p>
        {!seats ? (
          <p className="text-xs text-slate-500">載入中…</p>
        ) : !seats.ok ? (
          <p className="text-xs text-rose-400">⚠️ {seats.error}</p>
        ) : (
          <>
            <div className="flex flex-wrap gap-4 mb-3 text-xs">
              <span className="px-2 py-1 rounded bg-slate-900 border border-slate-700 text-slate-300">方案：{seats.planTier}</span>
              <span className="px-2 py-1 rounded bg-slate-900 border border-slate-700 text-slate-300">權益內含：{seats.includedSeats} 席</span>
              <span className="px-2 py-1 rounded bg-slate-900 border border-slate-700 text-slate-300">活躍 operator：{seats.activeOperators}</span>
              <span className="px-2 py-1 rounded bg-slate-900 border border-slate-700 text-slate-300">已購席：{seats.paidSeats}</span>
              <span className={`px-2 py-1 rounded border ${seats.billableSeats ? "bg-amber-900/30 border-amber-700/40 text-amber-300" : "bg-slate-900 border-slate-700 text-slate-300"}`}>
                待收費：{seats.billableSeats} 席
              </span>
            </div>

            {/* 席位帳號列表 */}
            <div className="overflow-x-auto mb-3">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-slate-500 border-b border-slate-800">
                    <th className="py-1.5 pr-3">用戶名</th>
                    <th className="py-1.5 pr-3">顯示名</th>
                    <th className="py-1.5 pr-3">角色</th>
                    <th className="py-1.5 pr-3">狀態</th>
                    <th className="py-1.5 pr-3">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {(seats.users || []).map((u) => (
                    <tr key={u.id} className="border-b border-slate-800/50 text-slate-300">
                      <td className="py-1.5 pr-3 font-mono">{u.username}</td>
                      <td className="py-1.5 pr-3">{u.display_name || "—"}</td>
                      <td className="py-1.5 pr-3">{u.role === "owner" ? "👤 owner" : "🧑‍💼 operator"}</td>
                      <td className="py-1.5 pr-3">
                        <span className={u.status === "active" ? "text-emerald-400" : "text-slate-500"}>
                          {u.status === "active" ? "✅ 啟用" : "⛔ 停用"}
                        </span>
                      </td>
                      <td className="py-1.5 pr-3">
                        {u.role === "operator" && (
                          <div className="flex gap-1.5">
                            <button type="button" disabled={seatsBusy} onClick={() => handleSeatAction("reset", { id: u.id }, `確定重設「${u.username}」密碼？舊密碼即刻作廢。`)} className="px-2 py-0.5 rounded bg-slate-800 hover:bg-slate-700 text-slate-300 disabled:opacity-50">🔑 重設密碼</button>
                            <button type="button" disabled={seatsBusy} onClick={() => handleSeatAction("status", { id: u.id, status: u.status === "active" ? "disabled" : "active" })} className="px-2 py-0.5 rounded bg-slate-800 hover:bg-slate-700 text-slate-300 disabled:opacity-50">{u.status === "active" ? "⛔ 停用" : "✅ 啟用"}</button>
                            <button type="button" disabled={seatsBusy} onClick={() => handleSeatAction("delete", { id: u.id }, `確定刪除「${u.username}」？此操作不可復原。`)} className="px-2 py-0.5 rounded bg-rose-900/40 hover:bg-rose-800/50 text-rose-300 disabled:opacity-50">🗑 刪除</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                  {!(seats.users || []).length && (
                    <tr><td colSpan={5} className="py-2 text-slate-500">未有席位帳號——下方開第一個。</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* 開新席位帳號 */}
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <input value={seatUsername} onChange={(e) => setSeatUsername(e.target.value)} placeholder="用戶名（3-30 位字母數字）" className="w-48 px-2 py-1.5 text-xs bg-slate-900 border border-slate-700 rounded-lg text-slate-200" />
              <input value={seatDisplayName} onChange={(e) => setSeatDisplayName(e.target.value)} placeholder="顯示名（可空）" className="w-40 px-2 py-1.5 text-xs bg-slate-900 border border-slate-700 rounded-lg text-slate-200" />
              <button type="button" onClick={handleSeatCreate} disabled={seatsBusy || !seatUsername.trim()} className="px-3 py-1.5 text-xs bg-violet-700 hover:bg-violet-600 disabled:bg-slate-700 text-white rounded-lg transition-colors">➕ 開席位帳號</button>
            </div>

            {/* 席位訂單（KPay） */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-slate-400">加購席位（超出權益）：</span>
              <select value={seatQty} onChange={(e) => setSeatQty(Number(e.target.value))} className="px-2 py-1.5 text-xs bg-slate-900 border border-slate-700 rounded-lg text-slate-200">
                {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n} 席（HK${n * 80}/月）</option>)}
              </select>
              <button type="button" onClick={() => handleSeatAction("order", { seats: seatQty }, `確定為「${tenantId}」開 ${seatQty} 席訂單（HK$${seatQty * 80}/月，KPay 收款）？`)} disabled={seatsBusy} className="px-3 py-1.5 text-xs bg-emerald-700 hover:bg-emerald-600 disabled:bg-slate-700 text-white rounded-lg transition-colors">💰 開 KPay 訂單</button>
            </div>
            {seatPayUrl && (
              <div className="mt-2 p-2 bg-slate-950 rounded-lg border border-emerald-700/30">
                <div className="flex items-center gap-2">
                  <p className="text-xs text-emerald-400 flex-1 break-all font-mono">{seatPayUrl}</p>
                  <button type="button" onClick={() => { navigator.clipboard.writeText(seatPayUrl).then(() => { setSeatPayCopied(true); setTimeout(() => setSeatPayCopied(false), 2000); }).catch(() => {}); }} className="px-2 py-1 text-xs bg-emerald-700 hover:bg-emerald-600 text-white rounded shrink-0">{seatPayCopied ? "已複製 ✓" : "📋 複製付款連結"}</button>
                </div>
              </div>
            )}

            {/* 訂單歷史 */}
            {(seats.orders || []).length > 0 && (
              <div className="mt-3">
                <p className="text-xs text-slate-500 mb-1">訂單（近 20 張）：</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-slate-500 border-b border-slate-800">
                        <th className="py-1 pr-3">訂單號</th>
                        <th className="py-1 pr-3">席數</th>
                        <th className="py-1 pr-3">金額</th>
                        <th className="py-1 pr-3">狀態</th>
                        <th className="py-1 pr-3">建立</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(seats.orders || []).map((o) => (
                        <tr key={o.order_id} className="border-b border-slate-800/50 text-slate-300 font-mono">
                          <td className="py-1 pr-3">{o.order_id}</td>
                          <td className="py-1 pr-3">{o.seats}</td>
                          <td className="py-1 pr-3">HK${o.amount_hkd}</td>
                          <td className="py-1 pr-3">
                            <span className={o.status === "paid" || o.status === "active" ? "text-emerald-400" : o.status === "pending" ? "text-amber-400" : "text-slate-500"}>
                              {o.status === "paid" ? "💰 已付" : o.status === "pending" ? "⏳ 待付" : o.status}
                            </span>
                          </td>
                          <td className="py-1 pr-3">{new Date(o.created_at).toLocaleString("en-GB", { timeZone: "Asia/Hong_Kong" })}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
        {seatsNote && (
          <p className={`text-xs mt-2 ${seatsNote.kind === "ok" ? "text-emerald-400" : "text-rose-400"}`}>{seatsNote.text}</p>
        )}
      </div>

      {/* 轉真人控制台連結 */}
      <div className="mb-6 p-4 bg-amber-900/20 border border-amber-800/30 rounded-xl">
        <h3 className="text-sm font-semibold text-amber-400 mb-2">
          🙋 轉真人控制台連結（給租戶客服人員使用）
        </h3>
        <p className="text-xs text-slate-400 mb-3">
          客服人員打開連結後，輸入登記手機號碼（下方「人工干預通知手機」欄位）的尾 4 位數字即可查看目前人工接管中的對話，
          直接點擊延長暫停／恢復 AI／查看對話，不用手打指令或客人電話號碼。重新產生會令舊連結立即失效。
        </p>
        {shortUrl ? (
          <>
            <div className="mb-2 p-3 bg-slate-950 rounded-lg border border-amber-700/30">
              <div className="text-xs text-amber-400 font-medium mb-1">🚀 短連結（推薦分享給客服）</div>
              <code className="block text-sm font-mono text-amber-300 break-all">
                {shortUrl}
              </code>
            </div>
            <button
              type="button"
              onClick={() => navigator.clipboard.writeText(shortUrl)}
              className="px-3 py-1.5 text-xs bg-amber-700/50 hover:bg-amber-700/70 text-amber-100 rounded-lg transition-colors mr-2"
            >
              📋 複製短連結
            </button>
            <details className="mt-2">
              <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-400">查看完整連結</summary>
              <code className="block mt-1 p-2 bg-slate-950 rounded text-xs font-mono text-slate-500 break-all">
                {WA_CONNECTOR_URL}/console/{tenantId}/{consoleToken}
              </code>
            </details>
          </>
        ) : null}
        <button
          type="button"
          onClick={handleGenerateConsoleLink}
          disabled={consoleLoading}
          className="px-3 py-1.5 text-xs bg-amber-700/40 hover:bg-amber-700/60 text-amber-200 rounded-lg transition-colors disabled:opacity-50"
        >
          {consoleLoading ? "產生中…" : shortUrl ? "🔄 重新產生連結" : "✨ 產生控制台連結"}
        </button>
        {consoleError && (
          <p className="text-xs text-rose-400 mt-2">⚠️ {consoleError}</p>
        )}
      </div>

      <div className="max-w-2xl bg-slate-900 rounded-xl border border-slate-800 p-6">
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Bot 名稱 */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">
              Bot 名稱
            </label>
            <input
              type="text"
              value={form.bot_name || ""}
              onChange={(e) => handleChange("bot_name", e.target.value)}
              className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 focus:outline-none focus:border-emerald-500"
            />
          </div>

          {/* 方案 */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">
              方案
            </label>
            <select
              value={form.plan_type || "trial"}
              onChange={(e) => handleChange("plan_type", e.target.value)}
              className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 focus:outline-none focus:border-emerald-500"
            >
              <option value="trial">trial — 試用</option>
              <option value="paid">paid — 付費</option>
              <option value="suspended">suspended — 停用</option>
            </select>
          </div>

          {/* 定價層級（任務 E） */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">
              定價層級
            </label>
            <select
              value={form.plan_tier || "legacy"}
              onChange={(e) => handleChange("plan_tier", e.target.value)}
              className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 focus:outline-none focus:border-emerald-500"
            >
              <option value="lite199">Lite — HK$199/月</option>
              <option value="pro499">Pro — HK$499/月</option>
              <option value="legacy">Legacy（視同 Pro 永久）</option>
            </select>
            <p className="text-xs text-slate-500 mt-1">
              Lite 頭兩個月 Premium 引擎，期滿自動降經濟引擎；Pro 永久 Premium。
            </p>
          </div>

          {/* 月度配額（2026-09-20 Captain 拍板：Lite 2000／Pro 6000；超額 HK$80/千則軟着陸月底結算） */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">
              月度配額（空＝跟方案）
            </label>
            <input
              type="number"
              min={0}
              value={form.monthly_quota_override ?? ""}
              onChange={(e) => handleChange("monthly_quota_override", e.target.value)}
              placeholder="Lite 2,000／Pro 6,000（legacy／試用＝無限額）"
              className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 placeholder-slate-500 focus:outline-none focus:border-emerald-500"
            />
            <p className="text-xs text-slate-500 mt-1">
              人手 recharge 落呢度（填大啲即時生效）；超額唔停服務，月底每 1,000 則 HK$80 結算。
            </p>
            {monthlyUsage && (monthlyUsage.rows?.[0] ?? null) && (
              <p className="text-xs text-slate-400 mt-1">
                本月（{monthlyUsage.ym}）：已回覆 <b>{String(monthlyUsage.rows![0].ai_replies)}</b> 則／
                配額 <b>{monthlyUsage.rows![0].quota === null ? "無限額" : String(monthlyUsage.rows![0].quota)}</b>
                {monthlyUsage.rows![0].overage > 0 ? `／超額 ${String(monthlyUsage.rows![0].overage)} 則（應收 HK$${String(monthlyUsage.rows![0].due_hkd)}）` : ""}
              </p>
            )}
          </div>

          {/* 渠道夥伴掛鉤（P1 §2.3） */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">
              渠道夥伴
            </label>
            <select
              value={form.partner_id || ""}
              onChange={(e) => handleChange("partner_id", e.target.value)}
              className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 focus:outline-none focus:border-emerald-500"
            >
              <option value="">無夥伴（自營客戶）</option>
              {partners.map((p) => (
                <option key={p.partner_id} value={p.partner_id}>
                  🤝 {p.name}（{p.partner_id}）
                </option>
              ))}
            </select>
            <p className="text-xs text-slate-500 mt-1">
              掛夥伴＝租戶歸因去該夥伴（批發結算）；新建夥伴租戶請喺「新增租戶」頁揀夥伴——嗰度會自動套用功能白名單。
            </p>
          </div>

          {/* Premium 體驗到期時間 */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">
              Premium 體驗到期時間
            </label>
            <input
              type="datetime-local"
              value={
                form.premium_until
                  ? new Date(form.premium_until).toISOString().slice(0, 16)
                  : ""
              }
              onChange={(e) => {
                const ms = e.target.value ? new Date(e.target.value).getTime() : 0;
                handleChange("premium_until", String(ms));
              }}
              className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 focus:outline-none focus:border-emerald-500"
            />
            <p className="text-xs text-slate-500 mt-1">
              Pro/Legacy 建議設 2100-01-01；Lite 預設兩個月後。留空＝0（已過期）。
            </p>
          </div>

          {/* 引擎提示：由方案層級決定 */}
          <div className="p-4 bg-slate-950/40 border border-slate-700/50 rounded-lg">
            <label className="block text-sm font-medium text-slate-300 mb-1">
              LLM 引擎
            </label>
            <p className="text-sm text-slate-200 mb-2">
              已由方案層級自動決定，毋須手動選擇。
            </p>
            <div className="text-xs text-slate-400 space-y-1">
              <p>
                • <span className="text-emerald-300">Lite</span>：頭兩個月 Premium（DeepSeek v4-flash＋識圖 vision-exp），期滿自動轉 Basic（GLM 免費層）
              </p>
              <p>
                • <span className="text-emerald-300">Pro / Legacy</span>：永久 Premium（DeepSeek 主力）
              </p>
              <p>
                • 詳細規則請見 <a href="#/engine-rules" className="text-sky-400 hover:underline">⚡ 引擎規則</a>
              </p>
            </div>
            {/* 保留隱藏欄位，避免更新時清掉舊值 */}
            <input type="hidden" value={form.llm_provider || ""} />
            <input type="hidden" value={form.llm_model || ""} />
          </div>

          {/* DeepSeek 自訂 Key（Vision 後備用） */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">
              DeepSeek 自訂 API Key
              <span className="ml-2 text-xs text-slate-500 font-normal">
                （Vision 後備／留空用系統預設）
              </span>
            </label>
            <input
              type="password"
              value={form.llm_api_key || ""}
              onChange={(e) => handleChange("llm_api_key", e.target.value)}
              placeholder="sk-... （留空使用系統預設）"
              className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 placeholder-slate-500 focus:outline-none focus:border-emerald-500 text-sm font-mono"
            />
            <p className="text-xs text-slate-500 mt-1">
              一般用系統預設 Key 即可；只有極少數 Vision 後備場景需要自訂。
            </p>
          </div>

          {/* 系統提示詞 */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-slate-300">
                系統提示詞（System Prompt）
              </label>
              <span className="text-xs text-slate-500">
                控制 Bot 的性格與行為準則
              </span>
            </div>

            {/* 預設模板按鈕 */}
            <div className="flex flex-wrap gap-2 mb-3">
              {PROMPT_TEMPLATES.map((tpl) => (
                <button
                  key={tpl.label}
                  type="button"
                  onClick={() => applyTemplate(tpl.prompt)}
                  className="px-3 py-1.5 text-xs bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-emerald-600 text-slate-300 hover:text-emerald-400 rounded-lg transition-colors"
                >
                  {tpl.icon} {tpl.label}
                </button>
              ))}
            </div>

            <textarea
              value={form.system_prompt || ""}
              onChange={(e) => handleChange("system_prompt", e.target.value)}
              rows={5}
              placeholder="輸入系統提示詞，或點選上方模板快速套用..."
              className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 focus:outline-none focus:border-emerald-500 text-sm"
            />
            <p className="text-xs text-slate-500 mt-1">
              每次對話開始時，這段文字會作為「背景指令」送給 AI，決定 Bot 如何回答客戶。
            </p>
          </div>

          {/* 人工干預通知手機 */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">
              人工干預通知手機
              <span className="ml-2 text-xs text-slate-500 font-normal">（最多 3 個，用逗號分隔）</span>
            </label>
            <input
              type="text"
              value={form.contact_handoff || ""}
              onChange={(e) => handleChange("contact_handoff", e.target.value)}
              placeholder="85291234567, 85298765432, 85251164453"
              className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 placeholder-slate-500 focus:outline-none focus:border-emerald-500"
            />
            <p className="text-xs text-slate-500 mt-1">
              當 AI 無法回應或需要轉真人時，系統會發 WhatsApp 通知給這些號碼，並自動暫停 AI 30 分鐘。
            </p>
          </div>

          {/* 主人號碼（AI 個人助理）——唯讀：由租戶在商戶中心經 WhatsApp 綁定碼自行綁定（v65） */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">
              主人號碼（AI 個人助理）
              <span className="ml-2 text-xs text-slate-500 font-normal">（唯讀——租戶於商戶中心「AI 個人助理」頁經 WhatsApp 綁定）</span>
            </label>
            <div className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-slate-300 font-mono text-sm">
              {form.owner_phone ? `+${form.owner_phone}` : "未綁定"}
            </div>
            <p className="text-xs text-slate-500 mt-1">
              綁定後助理只聽主人指令（對話講「我要錄音」即收一次性連結）。如需重置，請租戶於商戶中心按「重新綁定」。
            </p>
          </div>

          <hr className="border-slate-700" />

          {/* WhatsApp 配置 */}
          <div>
            <h3 className="text-sm font-semibold text-emerald-400 mb-3">
              📱 WhatsApp 配置（WhatsApp）
            </h3>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1">
                  WhatsApp API Key
                </label>
                <input
                  type="password"
                  value={form.whatsapp_api_key || ""}
                  onChange={(e) => handleChange("whatsapp_api_key", e.target.value)}
                  placeholder="留空＝保留現有 Key（只顯示尾 4 位）"
                  className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 placeholder-slate-500 focus:outline-none focus:border-emerald-500 text-sm font-mono"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1">
                  發送號碼（Sender Number）
                </label>
                <input
                  type="text"
                  value={form.whatsapp_sender_number || ""}
                  onChange={(e) => handleChange("whatsapp_sender_number", e.target.value)}
                  placeholder="85212345678"
                  className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 placeholder-slate-500 focus:outline-none focus:border-emerald-500 text-sm font-mono"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1">
                  WTS Webhook Endpoint（留空＝預設 cloudwapi.in）
                </label>
                <input
                  type="text"
                  value={form.rpmsms_base_url || ""}
                  onChange={(e) => handleChange("rpmsms_base_url", e.target.value)}
                  placeholder="https://unofficial.cloudwapi.in"
                  className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 placeholder-slate-500 focus:outline-none focus:border-emerald-500 text-sm font-mono"
                />
                <p className="mt-1 text-[11px] text-slate-500">
                  只填 base（路徑兩域一致）。留空或非法值＝用預設 unofficial.cloudwapi.in（2026-09-15 定調）；舊 rpmsms .com 帳號先明確填 https://unofficial.rpmsms.com。
                </p>
              </div>
            </div>
          </div>

          {/* 語音功能＋AI 秘書（角色化架構：雙開關；/merchant radio 二選一受此約束） */}
          <div className="p-4 bg-slate-950/40 border border-slate-700/50 rounded-lg space-y-3">
            <div className="text-sm font-semibold text-slate-200">🎙️ 語音功能／AI 秘書</div>
            <label className="flex items-center gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={Number(form.cs_enabled ?? 1) === 1}
                onChange={(e) => handleChange("cs_enabled", e.target.checked ? "1" : "0")}
                className="w-4 h-4 accent-emerald-500"
              />
              <span className="text-sm text-slate-300">AI 客服（cs_enabled）</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={Number(form.secretary_enabled ?? 0) === 1}
                onChange={(e) => handleChange("secretary_enabled", e.target.checked ? "1" : "0")}
                className="w-4 h-4 accent-emerald-500"
              />
              <span className="text-sm text-slate-300">AI 秘書（secretary_enabled——語音會議記錄／個人知識庫／跟進）</span>
            </label>
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1">AI 秘書工作習慣（人設；留空＝平台預設）</label>
              <textarea
                value={form.secretary_prompt || ""}
                onChange={(e) => handleChange("secretary_prompt", e.target.value)}
                rows={4}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 text-sm font-mono"
                placeholder="你係老闆嘅 AI 個人助理……（稱呼／語氣／跟進風格／匯報格式）"
              />
            </div>
            <label className="flex items-center gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={Number(form.vn_kb_ingest ?? 0) === 1}
                onChange={(e) => handleChange("vn_kb_ingest", e.target.checked ? "1" : "0")}
                className="w-4 h-4 accent-emerald-500"
              />
              <span className="text-sm text-slate-300">語音記錄完成後自動同步到團隊知識庫（vn_kb_ingest）</span>
            </label>
            <p className="text-[11px] text-slate-500">商戶端 /merchant 只可二選一；雙開僅限 Admin（角色化架構 §16）。記錄默認落個人知識庫；此開關控制額外同步到團隊 KB（客服 bot 可答）。</p>
            <label className="flex items-center gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={Number(form.role_chosen ?? 1) === 1}
                onChange={(e) => handleChange("role_chosen", e.target.checked ? "1" : "0")}
                className="w-4 h-4 accent-emerald-500"
              />
              <span className="text-sm text-slate-300">已選角色（role_chosen——關閉＝商戶下次登入重新顯示「請邊位 AI 員工」選擇頁）</span>
            </label>
          </div>

          {/* 回覆署名（兩層設計：開關 Off＝冇署名；On＋空＝平台預設；On＋有內容＝自訂） */}
          <div className="p-4 bg-slate-950/40 border border-slate-700/50 rounded-lg">
            <label className="flex items-center gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={brandOn}
                onChange={(e) => setBrandOn(e.target.checked)}
                className="w-4 h-4 accent-emerald-500"
              />
              <span className="text-sm font-semibold text-emerald-400">
                🏷️ 回覆署名（每則 AI 回覆最底一行）
              </span>
            </label>
            <p className="text-xs text-slate-500 mt-2 leading-relaxed">
              {brandOn
                ? "已啟用——下方留空＝用平台預設；填內容＝此租戶顯示自訂署名（覆蓋平台）。"
                : "未啟用——此租戶的客人不會看到任何署名。開啟後可選用平台預設或自訂內容。"}
            </p>
            {brandOn && (
              <div className="mt-3">
                <input
                  type="text"
                  value={brandText}
                  onChange={(e) => setBrandText(e.target.value)}
                  placeholder={`留空＝平台預設（${platformBrand}）`}
                  maxLength={120}
                  className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 placeholder-slate-500 focus:outline-none focus:border-emerald-500 text-sm font-mono"
                />
              </div>
            )}
          </div>

          {/* 報價單收款／品牌（quote_pdf 多客化） */}
          <div className="p-4 bg-slate-950/40 border border-slate-700/50 rounded-lg">
            <h3 className="text-sm font-semibold text-emerald-400 mb-2">
              🧾 報價單收款／品牌（自動報價單功能）
            </h3>
            <p className="text-xs text-slate-500 mb-3">
              開通「自動報價單」功能的租戶專用：控制 PDF 報價單的收款資料、頁腳與簽署公司名。
              全部留空＝使用平台預設版式（E-Corp）。
            </p>
            <div className="space-y-3">
              {BRANDING_FIELDS.map((bf) => (
                <div key={bf.key}>
                  <label className="block text-xs font-medium text-slate-400 mb-1">
                    {bf.label}
                  </label>
                  <input
                    type="text"
                    value={branding[bf.key] || ""}
                    onChange={(e) =>
                      setBranding((prev) => ({ ...prev, [bf.key]: e.target.value }))
                    }
                    placeholder={bf.placeholder}
                    className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 placeholder-slate-600 focus:outline-none focus:border-emerald-500 text-sm"
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-4 pt-2">
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-700 text-white font-medium rounded-lg transition-colors"
            >
              {saving ? "儲存中..." : "💾 儲存變更"}
            </button>
          </div>
        </form>

        {result && (
          <div
            className={`mt-6 p-4 rounded-lg ${
              result.success
                ? "bg-emerald-500/10 border border-emerald-500/30"
                : "bg-rose-500/10 border border-rose-500/30"
            }`}
          >
            <p
              className={`text-sm font-medium ${
                result.success ? "text-emerald-400" : "text-rose-400"
              }`}
            >
              {result.success
                ? `✅ 租戶 ${tenantId} 已更新（${result.updated} 個欄位）`
                : `❌ 錯誤：${result.error || "未知錯誤"}`}
            </p>
          </div>
        )}
      </div>
      </>)}
    </div>
  );
}
